import React, { Fragment, useEffect } from "react";

import AppPlaceholderPhoto from "@common/assets/images/shared-icons/app-icon-placeholder.svg";
import ChromeAppPlaceholderPhoto from "@common/assets/images/shared-icons/purple-app-icon-placeholder.svg";
import SlashIcon from "@common/assets/images/slash-icon-grey70.svg";
import { HelperText } from "@common/components/HelperText/HelperText";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import { App, isNativePlatform, PlatformName, Workspace } from "@common/models";
import { docsLinks, openDocsInNewTab } from "@common/util/docs";
import useWorkspaceAppsApiConsumer from "@main/consumers/useWorkspaceAppsApiConsumer";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { inviteModalHash } from "@main/pages/Administration/components/TeamMembersCard/TeamMembersCard";
import {
  AppImage,
  AppModuleHeader,
  AppNavContainer,
  AppNavigation,
  SlashImage,
} from "@main/pages/Administration/views/AppsView/AppsView.styles";
import { RoutePaths } from "@main/router/config/routePaths";
import { appsRouteConfigsWithKey, PathRouteConfig, RouteConfigType } from "@main/router/config/routesConfig";
import { generatePath, matchPath, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

export const AppsViewModule = () => {
  const { selectedWorkspace } = useAppSelectionContext();
  const { fetchAllApps } = useWorkspaceAppsApiConsumer();
  const { apps } = useAppContext();
  const { appKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedWorkspace) return;

    if (!apps || !apps.length) fetchAllApps(selectedWorkspace.id);
  }, [fetchAllApps, selectedWorkspace, apps]);

  useEffect(() => {
    if (!appKey && apps && apps.length) {
      const chosenApp = apps.find((a) => a.active) ? apps.find((a) => a.active) : apps[0];

      const route = !matchPath(RoutePaths.ADMINISTRATION_APPS, location.pathname)
        ? location.pathname + `/${chosenApp?.key}`
        : generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL_KEY, {
            workspaceSlug: selectedWorkspace?.slug ?? null,
            appKey: chosenApp?.key ?? null,
          });
      navigate(route);
    }
  }, [appKey, apps, navigate, selectedWorkspace, location.pathname]);

  if (!selectedWorkspace) {
    return <Spinner />;
  }

  const appsLinks = appsRouteConfigsWithKey
    .filter((routeConfig) => routeConfig.type === RouteConfigType.PATH)
    .map((pathRouteConfig) => {
      const config = pathRouteConfig as PathRouteConfig;
      const app = apps.find((app) => app.key === appKey);
      const isWeb = app?.platform.name === PlatformName.WEB;
      if (matchPath(config.path, RoutePaths.ADMINISTRATION_APPS_USERS_KEY) && app && isWeb) return;
      if (matchPath(config.path, RoutePaths.ADMINISTRATION_APPS_CRASHES_KEY) && app && !isNativePlatform(app)) return;

      return mapRouteConfigToNavLink(config, selectedWorkspace, !!matchPath(config.path, location.pathname), appKey);
    });

  const selectedApp: App | undefined = apps.find((app) => app.key === appKey);
  const appIcon: string | undefined = selectedApp?.logo_url
    ? selectedApp.logo_url
    : selectedApp?.is_extension
    ? ChromeAppPlaceholderPhoto
    : AppPlaceholderPhoto;

  return (
    <Fragment>
      <AppModuleHeader>
        <AppNavContainer>
          <AppImage src={appIcon} />
          <SlashImage src={SlashIcon} />
          <AppNavigation>{appsLinks}</AppNavigation>
        </AppNavContainer>
        <Paragraph p1>{"Switch tabs to explore all app settings: integrations, exporting..."}</Paragraph>
      </AppModuleHeader>
      <HelperText marginBottom={8}>
        Quick tips:
        <br />
        &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;{"Invite someone to this app only: "}
        <a
          onClick={() =>
            navigate(
              generatePath(RoutePaths.ADMINISTRATION_MEMBERS, {
                workspaceSlug: selectedWorkspace?.slug ?? null,
              }) + inviteModalHash,
            )
          }
        >
          {"Members"}
        </a>
        {" › Invite › select Guest role"}
        <br />
        &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;{"Pull in data from other tools: "}
        <a
          onClick={() =>
            navigate(
              generatePath(RoutePaths.ADMINISTRATION_EXTENSIONS, {
                workspaceSlug: selectedWorkspace?.slug ?? null,
              }),
            )
          }
        >
          {"Extensions"}
        </a>
        <br />
        &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;{"Build your own integration: "}
        <a onClick={() => openDocsInNewTab(docsLinks.generic.restApi)}>{"REST API"}</a>
        {" or "}
        <a onClick={() => openDocsInNewTab(docsLinks.generic.mcp)}>{"MCP server"}</a>
      </HelperText>
      <Outlet />
    </Fragment>
  );
};

const mapRouteConfigToNavLink = (config: PathRouteConfig, workspace: Workspace, isActive: boolean, appKey?: string) => {
  if (!appKey) return;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const to = generatePath(config.path, { workspaceSlug: workspace.slug ?? null, appKey: appKey ?? null });
  return (
    <div
      className={isActive ? "active" : ""}
      key={config.name}
    >
      <NavLink
        to={to}
        data-testid={config.testId}
      >
        {config.name}
      </NavLink>
    </div>
  );
};
